// AGC: Admin Panel - Inputs Master Catalog Configuration
// AGC: Manage agricultural inputs (herbicides, fungicides, fertilizers, seeds, etc.)
// AGC: CRUD operations for the global inputs database used in task templates

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
// AGC: [AR][INSUMOS] AR tab — renders the AR inputs catalog editor; Brazil's catalog below is unchanged.
import { CountrySwitcher, type ModelCountry } from './CountrySwitcher'
import { ArInsumosConfig } from './ArInsumosConfig'
import { getBackendUrl } from '../../../utils/backendUrl'
import { 
  type InputMaster, 
  type InputCategory,
  INPUT_CATEGORIES,
  getInputCategoryInfo,
} from '../../../types/inventory'
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  RefreshCw,
  Download,
  Upload,
  Filter,
  Package,
  AlertTriangle,
  Check,
} from 'lucide-react'

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const InputsMasterConfig: React.FC = () => {
  // AGC: [AR][INSUMOS] Country tab — 'AR' renders ArInsumosConfig; 'BR' keeps the existing catalog UI.
  const [country, setCountry] = useState<ModelCountry>('BR')
  const { t } = useTranslation()

  // AGC: State
  const [inputs, setInputs] = useState<InputMaster[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<InputCategory | 'all'>('all')
  const [showInactive, setShowInactive] = useState(false)
  const [configSource, setConfigSource] = useState<'defaults' | 'firestore'>('defaults')
  
  // AGC: Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingInput, setEditingInput] = useState<InputMaster | null>(null)
  const [formData, setFormData] = useState<Partial<InputMaster>>({})

  // AGC: Load inputs from API
  const loadInputs = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/inputs`, {
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load inputs: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      if (data.success) {
        setInputs(data.data.inputs || [])
        setConfigSource(data.data.source || 'defaults')
        console.log(`✅ AGC: [INPUTS_ADMIN] Loaded ${data.data.inputs?.length || 0} inputs`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.error('❌ AGC: [INPUTS_ADMIN] Error loading inputs:', message)
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  // AGC: Load on mount
  useEffect(() => {
    loadInputs()
  }, [loadInputs])

  // AGC: Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // AGC: Filter inputs
  const filteredInputs = inputs.filter(input => {
    const matchesSearch = searchTerm === '' || 
      input.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (input.activeIngredient?.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || input.category === selectedCategory
    const matchesActive = showInactive || input.isActive !== false
    return matchesSearch && matchesCategory && matchesActive
  })

  // AGC: Calculate stats
  const stats = {
    total: inputs.length,
    active: inputs.filter(i => i.isActive !== false).length,
    categories: [...new Set(inputs.map(i => i.category))].length,
    inactive: inputs.filter(i => i.isActive === false).length,
  }

  // AGC: Open modal for new/edit
  const handleOpenModal = (input?: InputMaster) => {
    if (input) {
      setEditingInput(input)
      setFormData(input)
    } else {
      setEditingInput(null)
      setFormData({
        name: '',
        category: 'herbicide',
        unit: 'L',
        defaultQuantityPerHa: 1,
        costPerUnit: 0,
        isActive: true,
      })
    }
    setIsModalOpen(true)
  }

  // AGC: Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingInput(null)
    setFormData({})
  }

  // AGC: Save input
  const handleSaveInput = async () => {
    if (!formData.name || !formData.category || !formData.unit) {
      setError('Please fill in all required fields')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const url = editingInput 
        ? `${backendUrl}/api/v1/admin/config/inputs/${editingInput.id}`
        : `${backendUrl}/api/v1/admin/config/inputs`
      
      const response = await fetch(url, {
        method: editingInput ? 'PUT' : 'POST',
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save input')
      }

      setSuccessMessage(editingInput ? 'Input updated successfully' : 'Input created successfully')
      handleCloseModal()
      loadInputs()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  // AGC: Delete input
  const handleDeleteInput = async (input: InputMaster) => {
    if (!confirm(`Are you sure you want to deactivate "${input.name}"?`)) {
      return
    }

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/inputs/${input.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer admin-token',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete input')
      }

      setSuccessMessage('Input deactivated successfully')
      loadInputs()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    }
  }

  // AGC: Seed default inputs
  const handleSeedDefaults = async () => {
    if (!confirm('This will add default inputs to the database. Continue?')) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/inputs/seed`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer admin-token',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to seed inputs')
      }

      setSuccessMessage(`Seeded ${data.data?.count || 0} default inputs`)
      loadInputs()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  // AGC: Export to CSV
  const handleExport = () => {
    const csvContent = [
      ['ID', 'Name', 'Category', 'Unit', 'Qty/Ha', 'Cost/Unit', 'Active Ingredient', 'Application', 'Safety Days', 'Active'].join(','),
      ...inputs.map(i => [
        i.id,
        `"${i.name}"`,
        i.category,
        i.unit,
        i.defaultQuantityPerHa,
        i.costPerUnit,
        `"${i.activeIngredient || ''}"`,
        i.applicationMethod || '',
        i.safetyIntervalDays || '',
        i.isActive !== false ? 'Yes' : 'No',
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'inputs-master-catalog.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // AGC: [AR][INSUMOS] Argentina tab renders its own inputs editor; Brazil keeps the catalog below.
  if (country === 'AR') {
    return (
      <div className="space-y-6">
        <CountrySwitcher value={country} onChange={setCountry} />
        <ArInsumosConfig />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <CountrySwitcher value={country} onChange={setCountry} />
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Package className="w-7 h-7 text-emerald-600" />
            Inputs Master Catalog
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage agricultural inputs used in task templates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full ${
            configSource === 'firestore' 
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
          }`}>
            {configSource === 'firestore' ? '🔥 Firestore' : '⚠️ Defaults'}
          </span>
        </div>
      </div>

      {/* AGC: Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">{successMessage}</span>
        </div>
      )}

      {/* AGC: Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={<Package className="w-5 h-5" />}
          value={stats.total}
          label="Total Inputs"
          color="#3B82F6"
        />
        <MetricCard
          icon={<Check className="w-5 h-5" />}
          value={stats.active}
          label="Active"
          color="#22C55E"
        />
        <MetricCard
          icon={<Filter className="w-5 h-5" />}
          value={stats.categories}
          label="Categories"
          color="#8B5CF6"
        />
        <MetricCard
          icon={<AlertTriangle className="w-5 h-5" />}
          value={stats.inactive}
          label="Inactive"
          color="#F59E0B"
        />
      </div>

      {/* AGC: Actions Bar */}
      <SectionCard title="Manage Inputs" icon={<Package className="w-5 h-5" />}>
        <div className="flex flex-wrap gap-4 mb-4">
          {/* AGC: Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search inputs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
              />
            </div>
          </div>

          {/* AGC: Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as InputCategory | 'all')}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
          >
            <option value="all">All Categories</option>
            {INPUT_CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.icon} {cat.namePt}</option>
            ))}
          </select>

          {/* AGC: Show Inactive Toggle */}
          <label className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Show Inactive</span>
          </label>

          {/* AGC: Action Buttons */}
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-brand-primary hover:bg-brand-dark dark:bg-brand-light dark:hover:opacity-90 dark:text-[#052e22] text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Input
          </button>
          <button
            onClick={loadInputs}
            disabled={loading}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleSeedDefaults}
            disabled={saving}
            className="px-4 py-2 border border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Seed Defaults
          </button>
        </div>

        {/* AGC: Inputs Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : filteredInputs.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('admin.noInputsFound')}</p>
            <button
              onClick={handleSeedDefaults}
              className="mt-4 text-emerald-600 hover:underline"
            >
              Seed default inputs
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-white/5">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Unit</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Qty/Ha</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Cost/Unit</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInputs.map((input) => {
                  const catInfo = getInputCategoryInfo(input.category)
                  return (
                    <tr 
                      key={input.id} 
                      className={`border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/[0.02] ${
                        input.isActive === false ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{input.name}</div>
                          {input.activeIngredient && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">{input.activeIngredient}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: `${catInfo?.color}20`, 
                            color: catInfo?.color 
                          }}
                        >
                          {catInfo?.icon} {catInfo?.namePt || input.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{input.unit}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">
                        {input.defaultQuantityPerHa}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">
                        R$ {input.costPerUnit?.toFixed(2) || '0.00'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          input.isActive !== false
                            ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300'
                        }`}>
                          {input.isActive !== false ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(input)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteInput(input)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                            title="Deactivate"
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* AGC: Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {editingInput ? 'Edit Input' : 'Add New Input'}
              </h3>
              <button onClick={handleCloseModal}>
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* AGC: Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  placeholder="e.g., Glifosato"
                />
              </div>

              {/* AGC: Category & Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category || 'herbicide'}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as InputCategory }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    {INPUT_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.namePt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Unit *
                  </label>
                  <select
                    value={formData.unit || 'L'}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    <option value="L">Liters (L)</option>
                    <option value="kg">Kilograms (kg)</option>
                    <option value="g">Grams (g)</option>
                    <option value="mL">Milliliters (mL)</option>
                    <option value="sacas">Bags (sacas)</option>
                    <option value="doses">Doses</option>
                    <option value="unidades">Units</option>
                  </select>
                </div>
              </div>

              {/* AGC: Quantity & Cost */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Default Quantity per Hectare
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.defaultQuantityPerHa || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, defaultQuantityPerHa: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Cost per Unit (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costPerUnit || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, costPerUnit: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                </div>
              </div>

              {/* AGC: Active Ingredient */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Active Ingredient
                </label>
                <input
                  type="text"
                  value={formData.activeIngredient || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, activeIngredient: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  placeholder="e.g., Glyphosate 480 g/L"
                />
              </div>

              {/* AGC: Application Method & Safety Interval */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Application Method
                  </label>
                  <select
                    value={formData.applicationMethod || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, applicationMethod: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    <option value="">Select...</option>
                    <option value="spray">Spray</option>
                    <option value="soil">Soil Application</option>
                    <option value="seed_treatment">Seed Treatment</option>
                    <option value="foliar">Foliar</option>
                    <option value="planting">Planting</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Safety Interval (days)
                  </label>
                  <input
                    type="number"
                    value={formData.safetyIntervalDays || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, safetyIntervalDays: parseInt(e.target.value) || undefined }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    placeholder="Days before harvest"
                  />
                </div>
              </div>

              {/* AGC: Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive !== false}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="isActive" className="text-sm text-slate-700 dark:text-slate-300">
                  Active (available for use in task templates)
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInput}
                disabled={saving}
                className="px-4 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] hover:bg-brand-dark dark:hover:opacity-90 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingInput ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InputsMasterConfig

